import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';

interface Ministry {
  id: number;
  name: string;
  code?: string;
  isActive?: boolean;
}

const AdminUsers = () => {
  const [ministries, setMinistries] = useState<Ministry[]>([]);
  const [newMinistry, setNewMinistry] = useState({ name: '' });
  const [admins, setAdmins] = useState<any[]>([]);
  const [form, setForm] = useState({
    ministry_name: '',
    ministry_id: '',
    fullName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
  });

  const [savingAdmin, setSavingAdmin] = useState(false);
  const [savingMinistry, setSavingMinistry] = useState(false);
  const [loading, setLoading] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  // Password reset state
  const [resetUserId, setResetUserId] = useState<number | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Admin toggle state
  const [togglingAdminId, setTogglingAdminId] = useState<number | null>(null);

  // Fetch ministries
  const loadMinistries = async () => {
    try {
      setLoading(true);
      const res = await api.get('/ministries');
      setMinistries(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load ministries');
    } finally {
      setLoading(false);
    }
  };

  // Fetch admins
  const loadAdmins = async () => {
    try {
      const res = await api.get('/auth/getRegisterAdmin');
      setAdmins(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load admins');
    }
  };

  useEffect(() => {
    loadMinistries();
    loadAdmins();
  }, []);

  // Create ministry
  const handleCreateMinistry = async () => {
    if (!newMinistry.name.trim()) {
      toast.error('Fill all fields');
      return;
    }
    try {
      setSavingMinistry(true);
      await api.post('/ministries', { name: newMinistry.name.trim() });
      toast.success('Ministry created');
      setNewMinistry({ name: '' });
      await loadMinistries();
    } catch (err) {
      console.error(err);
      toast.error('Failed to create ministry');
    } finally {
      setSavingMinistry(false);
    }
  };

  // Create admin
  const handleCreateAdmin = async () => {
    if (!form.ministry_name || !form.fullName || !form.email || !form.password) {
      toast.error('Fill all fields');
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      setSavingAdmin(true);
      await api.post('/auth/register', { ...form, role: 'ministry_admin' });
      toast.success('Ministry admin created');
      setForm({ ministry_name: '', ministry_id: '', fullName: '', email: '', phoneNumber: '', password: '', confirmPassword: '' });
      await loadAdmins();
    } catch (err) {
      console.error(err);
      toast.error('Failed to create admin');
    } finally {
      setSavingAdmin(false);
    }
  };

  // start inline edit
  const startEdit = (m: Ministry) => {
    setEditingId(m.id);
    setEditingName(m.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  // update ministry name
  const handleUpdateMinistry = async (id: number) => {
    if (!editingName.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      setUpdatingId(id);
      const res = await api.patch(`/ministries/${id}`, { name: editingName.trim() });
      const updated: Ministry = res.data;
      setMinistries(prev => prev.map(m => (m.id === id ? updated : m)));
      toast.success('Ministry updated');
      cancelEdit();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update ministry');
    } finally {
      setUpdatingId(null);
    }
  };

  // toggle active state
  const handleToggleActive = async (id: number, current?: boolean) => {
    try {
      setTogglingId(id);
      const res = await api.patch(`/ministries/${id}`, { isActive: !current });
      const updated: Ministry = res.data;
      setMinistries(prev => prev.map(m => (m.id === id ? updated : m)));
      toast.success(updated.isActive ? 'Activated' : 'Deactivated');
    } catch (err) {
      console.error(err);
      toast.error('Failed to toggle status');
    } finally {
      setTogglingId(null);
    }
  };

  // Reset password for a user
  const handleResetPassword = async () => {
    if (!resetPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (resetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (resetPassword !== resetConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      setResettingPassword(true);
      await api.post('/auth/reset-password', { userId: resetUserId, newPassword: resetPassword });
      toast.success('Password reset successfully');
      setResetUserId(null);
      setResetPassword('');
      setResetConfirmPassword('');
      setShowPassword(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  // Toggle admin active/inactive
  const handleToggleAdminActive = async (userId: number, currentStatus: boolean) => {
    try {
      setTogglingAdminId(userId);
      await api.patch(`/auth/users/${userId}`, { isActive: !currentStatus });
      toast.success(!currentStatus ? 'User activated' : 'User deactivated');
      await loadAdmins();
    } catch (err) {
      console.error(err);
      toast.error('Failed to toggle user status');
    } finally {
      setTogglingAdminId(null);
    }
  };

  return (
    <div className="p-6">
      {/* Ministry Form */}
      <div className="bg-white shadow rounded p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Create Ministry</h2>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <input
            className="input"
            placeholder="Ministry Name"
            value={newMinistry.name}
            onChange={e => setNewMinistry({ ...newMinistry, name: e.target.value })}
          />
        </div>
        <button
          className="btn-primary mt-4"
          onClick={handleCreateMinistry}
          disabled={savingMinistry}
        >
          {savingMinistry ? 'Creating…' : 'Create Ministry'}
        </button>
      </div>

      {/* Ministries table */}
      <div className="bg-white shadow rounded p-6">
        <h2 className="text-lg font-semibold mb-4">Ministries</h2>

        {loading ? (
          <div>Loading…</div>
        ) : ministries.length === 0 ? (
          <div className="text-sm text-gray-500">No ministries found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="text-left text-gray-600">
                  <th className="py-2 px-3">Ministry Name</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">Update</th>
                  <th className="py-2 px-3">Activate / Deactivate</th>
                </tr>
              </thead>
              <tbody>
                {ministries.map((m) => (
                  <tr key={m.id} className="border-t">
                    <td className="py-2 px-3">
                      {editingId === m.id ? (
                        <input
                          className="input"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                        />
                      ) : (
                        <span>{m.name}</span>
                      )}
                    </td>

                    <td className="py-2 px-3">
                      <span className={`inline-block px-2 py-1 rounded text-sm ${m.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {m.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>

                    <td className="py-2 px-3">
                      {editingId === m.id ? (
                        <div className="flex gap-2">
                          <button
                            className="btn-primary"
                            onClick={() => handleUpdateMinistry(m.id)}
                            disabled={updatingId === m.id}
                          >
                            {updatingId === m.id ? 'Saving…' : 'Save'}
                          </button>
                          <button className="btn-outline" onClick={cancelEdit}>Cancel</button>
                        </div>
                      ) : (
                        <button className="btn-outline" onClick={() => startEdit(m)}>Edit</button>
                      )}
                    </td>

                    <td className="py-2 px-3">
                      <button
                        className="btn-secondary"
                        onClick={() => handleToggleActive(m.id, m.isActive)}
                        disabled={togglingId === m.id}
                      >
                        {togglingId === m.id ? 'Processing…' : (m.isActive ? 'Deactivate' : 'Activate')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Admin Form */}
      <div className="bg-white shadow rounded p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">Create Ministry Admin</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <select
            className="input"
            value={form.ministry_id}
            onChange={e => {
              const selectedId = e.target.value;
              const selectedMinistry = ministries.find(m => m.id.toString() === selectedId);
              setForm({ 
                ...form, 
                ministry_id: selectedId,
                ministry_name: selectedMinistry ? selectedMinistry.name : ''
              });
            }}
          >
            <option value="">Select Ministry</option>
            {ministries
              .filter(m => m.isActive) // ✅ Only show active ministries
              .map(m => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
          </select>

          <input className="input" placeholder="Full Name" value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })} />
          <input className="input" placeholder="Phone" value={form.phoneNumber} onChange={e => setForm({ ...form, phoneNumber: e.target.value })} />
          <input className="input" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          <input className="input" placeholder="Password" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
          <input className="input" placeholder="Confirm Password" type="password" value={form.confirmPassword} onChange={e => setForm({ ...form, confirmPassword: e.target.value })} />
        </div>
        <button className="btn-primary mt-4" onClick={handleCreateAdmin} disabled={savingAdmin}>
          {savingAdmin ? 'Creating…' : 'Create Ministry Admin'}
        </button>
      </div>

      {/* Admin Table */}
      <div className="bg-white shadow rounded p-6">
        <h2 className="text-lg font-semibold mb-4">Ministry Admins</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="text-left py-2 px-3">Name</th>
                <th className="text-left py-2 px-3">Email</th>
                <th className="text-left py-2 px-3">Phone</th>
                <th className="text-left py-2 px-3">Ministry</th>
                <th className="text-left py-2 px-3">Role</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map(a => (
                <tr key={a.id} className="border-t">
                  <td className="py-2 px-3">{a.fullName}</td>
                  <td className="py-2 px-3">{a.email}</td>
                  <td className="py-2 px-3">{a.phoneNumber || '-'}</td>
                  <td className="py-2 px-3">{a.ministry_name || '-'}</td>
                  <td className="py-2 px-3">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      a.role === 'super_admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {a.role === 'super_admin' ? 'Super Admin' : a.role === 'ministry_admin' ? 'Ministry Admin' : a.role}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      a.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {a.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex gap-2 flex-wrap">
                      <button
                        className="px-3 py-1 text-sm rounded bg-amber-500 hover:bg-amber-600 text-white transition-colors"
                        onClick={() => {
                          setResetUserId(a.id);
                          setResetPassword('');
                          setResetConfirmPassword('');
                          setShowPassword(false);
                        }}
                      >
                        Set Password
                      </button>
                      <button
                        className={`px-3 py-1 text-sm rounded transition-colors ${
                          a.isActive
                            ? 'bg-red-500 hover:bg-red-600 text-white'
                            : 'bg-green-500 hover:bg-green-600 text-white'
                        }`}
                        onClick={() => handleToggleAdminActive(a.id, a.isActive)}
                        disabled={togglingAdminId === a.id}
                      >
                        {togglingAdminId === a.id ? 'Processing…' : (a.isActive ? 'Deactivate' : 'Activate')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Password Reset Modal */}
      {resetUserId !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-1">Set New Password</h3>
            <p className="text-sm text-gray-500 mb-4">
              For: <span className="font-medium text-gray-700">{admins.find(a => a.id === resetUserId)?.fullName}</span>
              {' '}({admins.find(a => a.id === resetUserId)?.email})
            </p>

            <div className="space-y-3">
              <div className="relative">
                <input
                  className="input w-full pr-10"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="New Password"
                  value={resetPassword}
                  onChange={e => setResetPassword(e.target.value)}
                  autoFocus
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              <input
                className="input w-full"
                type={showPassword ? 'text' : 'password'}
                placeholder="Confirm New Password"
                value={resetConfirmPassword}
                onChange={e => setResetConfirmPassword(e.target.value)}
              />
            </div>

            <div className="flex gap-3 mt-5 justify-end">
              <button
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setResetUserId(null);
                  setResetPassword('');
                  setResetConfirmPassword('');
                  setShowPassword(false);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-600 text-white transition-colors disabled:opacity-50"
                onClick={handleResetPassword}
                disabled={resettingPassword}
              >
                {resettingPassword ? 'Resetting…' : 'Set Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;

